import React from "react";
import { CARD_BORDER, NAVY } from "./DebtCollectionSectionConstants";

/**
 * Decorative stacked-cards SVG for the empty debt configuration state.
 * @returns {JSX.Element} Inline SVG icon.
 */
export function EmptyStateCardsIcon() {
  return (
    <svg
      width="120"
      height="88"
      viewBox="0 0 120 88"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="mx-auto d-block text-muted"
      aria-hidden
    >
      <rect
        x="8"
        y="20"
        width="72"
        height="52"
        rx="8"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="#f8f9fa"
        opacity="0.9"
      />
      <rect
        x="28"
        y="12"
        width="72"
        height="52"
        rx="8"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="#fff"
        opacity="0.95"
      />
      <rect
        x="48"
        y="4"
        width="64"
        height="48"
        rx="8"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="#fff"
      />
    </svg>
  );
}

/**
 * Small stat card used in the debt configuration summary grid.
 * @param {object} props - Tile props.
 * @param {string} props.label - Heading text.
 * @param {React.ReactNode} props.value - Primary value.
 * @param {boolean} [props.large] - Taller minimum height when true.
 * @param {boolean} [props.bordered] - Bordered white card vs borderless light background.
 * @returns {JSX.Element} Card fragment.
 */
export function SummaryTile({ label, value, large, bordered }) {
  return (
    <div
      className={`card rounded-lg p-3 h-100 ${
        bordered ? "bg-white" : "border-0 bg-light"
      }`}
      style={{
        minHeight: large ? "100px" : "auto",
        border: bordered ? `1px solid ${CARD_BORDER}` : undefined,
      }}
    >
      <div className="debt-summary-tile-label text-muted mb-1">{label}</div>
      <div className="debt-summary-tile-value text-dark mb-0">
        {value ?? "—"}
      </div>
    </div>
  );
}

/**
 * Bordered panel with title and body for debt section side columns.
 * @param {object} props - Panel props.
 * @param {string} props.title - Panel heading.
 * @param {React.ReactNode} props.children - Body content.
 * @param {string} [props.className="h-100"] - Extra class names for the wrapper.
 * @returns {JSX.Element} Panel layout.
 */
export function InfoPanel({ title, children, className = "h-100" }) {
  return (
    <div
      className={`debt-info-panel rounded-lg p-3 mb-3 mb-lg-0 bg-white d-flex flex-column ${className}`.trim()}
      style={{
        border: `1px solid ${CARD_BORDER}`,
      }}
    >
      <div
        className="debt-info-panel-title mb-2 flex-shrink-0"
        style={{ color: NAVY }}
      >
        {title}
      </div>
      <div
        className="debt-info-panel-body flex-grow-1"
        style={{ minHeight: 0 }}
      >
        {children}
      </div>
    </div>
  );
}
